import { Storage } from '../lib/storage';
import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Bell, Shield, Wallet, Smartphone, ChevronRight, Palette, Edit3, Check, RefreshCw, LogOut, BookOpen, Lock, AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function ConfiguracionScreen({ appTheme, setAppTheme, onOpenSOSConfig, onOpenApodos, onOpenNotificaciones, onOpenAyuda, unreadCount, onOpenCrearPIN }: { appTheme?: string, setAppTheme?: (t: string) => void, onOpenSOSConfig?: () => void, onOpenApodos?: () => void, onOpenNotificaciones?: () => void, onOpenAyuda?: () => void, unreadCount?: number, onOpenCrearPIN?: () => void }) {
  const themesList = [
    { id: 'oled', name: 'Negro OLED', hex: '#000000' },
    { id: 'gris', name: 'Gris Claro', hex: '#475569' },
    { id: 'titanio', name: 'Azul Titanio', hex: '#0a1128' },
    { id: 'amatista', name: 'Rosa Amatista', hex: '#291322' },
    { id: 'militar', name: 'Verde Militar', hex: '#111f14' }
  ];
  const [sincronizando, setSincronizando] = useState(false);
  const [slideValue, setSlideValue] = useState(0);
  const [mostrarConfirmarBaja, setMostrarConfirmarBaja] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const userStr = Storage.get('motopikis_user');
  const soyEmpleado = userStr ? JSON.parse(userStr).rol === 'empleado_taller' : false;

  const procesarSalidaSegura = async () => {
    // Solo borramos lo necesario para la sesión, respetando métricas que ahora están en Supabase
    Storage.remove('motopikis_user');
    Storage.remove('motopikis_user_id');
    Storage.remove('motopikis_token');
    Storage.remove('motopikis_onboarding_done');
    Storage.remove('motopikis_sos_fam1Nombre');
    Storage.remove('motopikis_sos_fam1Celular');
    Storage.remove('motopikis_sos_motero1Nombre');
    Storage.remove('motopikis_sos_motero1Celular');
    Storage.remove('motopikis_sos_motero2Nombre');
    Storage.remove('motopikis_sos_motero2Celular');
    Storage.remove('motopikis_sos_motero3Nombre');
    Storage.remove('motopikis_sos_motero3Celular');
    sessionStorage.clear();
    await supabase.auth.signOut();
    window.location.reload();
  };

  const ejecutarPurgaTotal = async () => {
    setEliminando(true);
    const myId = Storage.get('motopikis_user_id');
    if (!myId) {
      procesarSalidaSegura();
      return;
    }

    try {
      // 1. Borrar Actividad Social y Notificaciones
      await supabase.from('notificaciones_inapp').delete().eq('usuario_id', myId);
      await supabase.from('alertas_sos').delete().eq('usuario_id', myId);
      await supabase.from('chat_escuadrones').delete().eq('emisor_id', myId);
      await supabase.from('asistentes_rutas').delete().eq('motero_id', myId);
      
      // 2. Borrar Relaciones de Parche y Escuadrones
      await supabase.from('amigos_parche').delete().eq('anfitrion_id', myId);
      await supabase.from('miembros_escuadron').delete().eq('motero_id', myId);
      
      // 3. Borrar Datos de Talleres y Mercado
      await supabase.from('solicitudes_taller').delete().eq('usuario_id', myId);
      await supabase.from('cotizaciones').delete().eq('motero_id', myId);
      await supabase.from('t_concesionario').delete().eq('usuario_id', myId);
      await supabase.from('t_reportes_mercado').delete().eq('usuario_id', myId);
      
      // 4. Borrar Vehículos (Bóveda)
      await supabase.from('vehiculos').delete().eq('usuario_id', myId);

      // 5. Intentar borrar escuadrones donde sea el capitán (opcional, para limpieza profunda)
      await supabase.from('escuadrones').delete().eq('capitan_id', myId);
      
      // 6. Borrar el Perfil de Usuario (Final)
      await supabase.from('usuarios').delete().eq('id', myId);
      
      procesarSalidaSegura();
    } catch (err: any) {
      console.error("Error en purga:", err);
      // Intentamos cerrar sesión de todos modos para que el usuario no quede atrapado
      procesarSalidaSegura();
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col min-h-full"
    >
      <h2 className="text-xl font-extrabold mb-1 flex items-center gap-2">
        <Settings className="w-6 h-6 text-primary-cyan" /> Configuración
      </h2>
      <p className="text-text-muted text-[13px] border-b border-white/5 pb-4 mb-6">Administra tus preferencias de cuenta.</p>

      {/* Apodos extraídos al Modal */}

      <div className="space-y-4 pb-12">

        {/* SLIDE TO LOGOUT (NATURAL HTML5 ENGINE) */}

        {!soyEmpleado && (
        <>
          <button 
            onClick={onOpenCrearPIN}
            className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-primary-cyan/20 hover:bg-primary-cyan/10 transition-colors shadow-[0_0_15px_rgba(0,168,255,0.05)] mt-3 mb-2"
          >
            <div className="flex items-center gap-3">
              <div className="bg-primary-cyan/20 p-2 rounded-lg"><Lock className="w-5 h-5 text-primary-cyan" /></div>
              <div className="flex flex-col items-start min-w-0">
                 <span className="font-black text-[15px] text-primary-cyan tracking-wide">Crear / Cambiar Clave</span>
                 <span className="text-[10px] text-slate-400 font-bold uppercase">Bóveda de Seguridad</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-primary-cyan" />
          </button>
        </>
        )}

        <button 
          onClick={onOpenAyuda}
          className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-primary-cyan/20 hover:bg-slate-800/80 transition-colors shadow-[0_0_15px_rgba(0,168,255,0.05)] mt-3 mb-2"
        >
          <div className="flex items-center gap-3">
            <div className="bg-primary-cyan/10 p-2 rounded-lg border border-primary-cyan/30">
              <BookOpen className="w-5 h-5 text-primary-cyan" />
            </div>
            <div className="flex flex-col items-start min-w-0">
               <span className="font-black text-[14px] text-white tracking-wide">Manual del Sistema</span>
               <span className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">Aprende a usar la red</span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-500" />
        </button>

        {!soyEmpleado && (
        <>
          <div className="w-full bg-slate-800/50 p-3 flex flex-col rounded-xl border border-white/5 shadow-md">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-orange-500/20 p-2 rounded-lg"><Palette className="w-5 h-5 text-orange-400" /></div>
                <div className="flex flex-col items-start gap-1">
                  <span className="font-black text-[15px] text-white tracking-wide">Tema de Fondo</span>
                  <span className="text-[11px] text-text-muted leading-none font-medium">Personaliza la app</span>
                </div>
              </div>
              <span className="text-[10px] bg-white/10 px-3 py-1 rounded-full text-text-muted font-bold tracking-widest uppercase">
                {themesList.find(t => t.id === appTheme)?.name || 'Negro OLED'}
              </span>
            </div>
            <div className="flex justify-between items-center px-1">
              {themesList.map((t) => (
                <button 
                  key={t.id} 
                  onClick={() => setAppTheme && setAppTheme(t.id)}
                  title={t.name}
                  className={`w-9 h-9 rounded-full border-[1.5px] transition-all shadow-md ${appTheme === t.id ? 'border-primary-cyan scale-110 shadow-[0_0_10px_rgba(0,168,255,0.4)] ring-2 ring-primary-cyan/20' : 'border-white/20 hover:scale-105 opacity-80'}`}
                  style={{ backgroundColor: t.hex }}
                />
              ))}
            </div>
          </div>

          <button 
            onClick={onOpenApodos}
            className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-white/5 hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="bg-blue-500/20 p-2 rounded-lg"><Edit3 className="w-5 h-5 text-blue-400" /></div>
              <span className="font-bold text-[15px] text-white">Apoda tus Vehículos</span>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-500" />
          </button>

          <button 
            onClick={onOpenNotificaciones}
            className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-white/5 hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="bg-primary-cyan/20 p-2 rounded-lg">
                <Bell className="w-5 h-5 text-primary-cyan" />
              </div>
              <span className="font-bold text-[15px] text-white">Notificaciones / Ofertas</span>
            </div>
            
            <div className="flex items-center gap-2">
              {typeof unreadCount === 'number' && (
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${unreadCount > 0 ? 'bg-danger-red text-white shadow-[0_0_10px_rgba(239,68,68,0.5)] animate-pulse' : 'bg-white/10 text-slate-400'}`}>
                  {unreadCount}
                </span>
              )}
              <ChevronRight className="w-5 h-5 text-slate-500" />
            </div>
          </button>
        </>
        )}


        {!soyEmpleado && (
        <>
          <button 
            onClick={onOpenSOSConfig}
            className="w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border border-white/5 hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="bg-green-500/20 p-2 rounded-lg"><Shield className="w-5 h-5 text-green-400" /></div>
              <span className="font-bold text-[15px] text-white">Seguridad y SOS</span>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-500" />
          </button>
        </>
        )}

        {/* FORZAR SINCRONIZACIÓN (MOVIDO HACIA ABAJO) */}
        <button 
          onClick={async () => {
            setSincronizando(true);
            
            // INTELIGENCIA DE LIMPIEZA: Borrar todas las cachés antes de recargar
            if ('caches' in window) {
              try {
                const keys = await caches.keys();
                await Promise.all(keys.map(key => caches.delete(key)));
                console.log("[Sync] Cachés purgadas con éxito");
              } catch (e) {
                console.error("[Sync] Error al purgar cachés:", e);
              }
            }

            setTimeout(() => {
              window.location.href = window.location.origin + window.location.pathname + '?rnd=' + new Date().getTime();
            }, 1000);
          }}
          disabled={sincronizando}
          className={`w-full bg-slate-800/50 px-4 py-3.5 flex justify-between items-center rounded-xl border transition-all mt-6 ${sincronizando ? 'border-primary-cyan/50 bg-primary-cyan/5 shadow-[0_0_15px_rgba(0,168,255,0.2)] scale-[0.98]' : 'border-white/5 hover:bg-slate-800'}`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg transition-colors ${sincronizando ? 'bg-primary-cyan shadow-[0_0_10px_rgba(0,168,255,0.5)]' : 'bg-rose-500/20'}`}>
              <RefreshCw className={`w-5 h-5 ${sincronizando ? 'text-white animate-spin' : 'text-rose-400'}`} />
            </div>
            <span className={`font-bold text-[15px] transition-colors ${sincronizando ? 'text-primary-cyan' : 'text-white'}`}>
              {sincronizando ? 'Sincronizando Nube...' : 'Forzar Sincronización de Nube'}
            </span>
          </div>
          {sincronizando ? <Check className="w-5 h-5 text-primary-cyan animate-pulse" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
        </button>


        {/* SLIDE TO LOGOUT (MOVIDO AL FINAL) */}
        <div className="w-full h-[64px] bg-[#160a0a] rounded-2xl border border-danger-red/30 px-1.5 relative overflow-hidden shadow-inner mt-4 mb-2 flex items-center">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-danger-red/40 font-black text-[12px] uppercase tracking-widest pl-10 shimmer-text">Cerrar Sesión &gt;&gt;</span>
          </div>
          
          <div 
            className="w-[52px] h-[52px] bg-gradient-to-br from-red-600 to-red-800 rounded-xl absolute z-10 flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.5)] pointer-events-none transition-transform duration-[50ms]"
            style={{ left: `calc(6px + (100% - 64px) * ${slideValue / 100})` }}
          >
            <LogOut className="w-6 h-6 text-white ml-1 drop-shadow-md" />
          </div>

          <input 
            type="range"
            min="0"
            max="100"
            value={slideValue}
            onChange={(e) => {
               const val = Number(e.target.value);
               setSlideValue(val);
               if (val >= 95) {
                  procesarSalidaSegura();
               }
            }}
            onMouseUp={() => { if(slideValue < 95) setSlideValue(0); }}
            onTouchEnd={() => { if(slideValue < 95) setSlideValue(0); }}
            className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-e-resize"
          />
        </div>

        {/* BOTÓN ELIMINAR CUENTA (PLAY STORE COMPLIANCE) */}
        <button 
          onClick={() => setMostrarConfirmarBaja(true)}
          className="w-full py-2 text-red-500/30 hover:text-red-500 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all mt-6"
        >
          Eliminar Mi Cuenta y Datos
        </button>
      </div>

      <AnimatePresence>
        {mostrarConfirmarBaja && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[500] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-sm bg-[#0B0F19] border border-red-500/30 rounded-[35px] p-8 text-center shadow-[0_0_50px_rgba(239,68,68,0.2)] relative overflow-hidden"
            >
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50"></div>
              
              <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/20 shadow-inner">
                <AlertTriangle className="w-10 h-10 text-red-500 animate-pulse" />
              </div>
              
              <h3 className="text-white font-black text-xl mb-3 tracking-tighter uppercase">¿Exterminar Expediente?</h3>
              <p className="text-slate-400 text-[13px] leading-relaxed mb-8">
                Esta acción purgará tu historial, fotos y registros de la base de datos de forma permanente. <strong className="text-red-400">No hay marcha atrás.</strong>
              </p>
              
              <div className="flex flex-col gap-3">
                <button 
                  onClick={ejecutarPurgaTotal}
                  disabled={eliminando}
                  className="w-full py-4 bg-red-600 hover:bg-red-500 text-white font-black rounded-2xl text-[12px] uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-red-600/20"
                >
                  {eliminando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                  {eliminando ? 'Purgando Datos...' : 'Sí, Eliminar Todo'}
                </button>
                
                <button 
                  onClick={() => setMostrarConfirmarBaja(false)}
                  disabled={eliminando}
                  className="w-full py-4 bg-white/5 hover:bg-white/10 text-slate-400 font-bold rounded-2xl text-[11px] uppercase tracking-widest transition-all"
                >
                  Cancelar Protocolo
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-2 pt-4 border-t border-white/5 text-center opacity-20 pb-6">
         <p className="text-[10px] font-black uppercase tracking-widest text-white">
          MOTOPikis Pro - Powered By <a href="https://www.pikishub.com" target="_blank" rel="noreferrer" className="underline hover:text-primary-cyan transition-colors">PIKIS HUB</a>
         </p>
      </div>
    </motion.div>
  );
}
